import React from "react";
import { FOCUS_RING } from "../utils/a11y";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useIsHydrated } from "../hooks/useIsHydrated";
import bgServices from "../../assets/services/bg-services-blured.webp";
import imgServiceDevelopment from "../../assets/services/service-development.webp";
import imgServiceDesign from "../../assets/services/service-design.webp";
import imgServiceManagement from "../../assets/services/service-management.webp";
import imgServiceOutsourcing from "../../assets/services/service-outsourcing.webp";
import imgServiceRework from "../../assets/services/service-rework.webp";
import imgServiceConsult from "../../assets/services/service-consult.webp";
import Atropos from "atropos/react";

const SERVICES = [
  { name: "Development", image: imgServiceDevelopment },
  { name: "Design & Vision", image: imgServiceDesign },
  { name: "Operation", image: imgServiceManagement },
  { name: "Outsource", image: imgServiceOutsourcing },
  { name: "Rework", image: imgServiceRework },
  { name: "Consultation", image: imgServiceConsult },
];

export default function WhatWeDo({ onServiceClicked, sectionRef }) {
  /* One media-query listener for the whole grid. */
  const isLargeScreen = useMediaQuery("(min-width: 1024px)");
  /* Atropos is a pointer-driven 3D tilt — exactly the kind of motion someone
   * who asked for less of it does not want. */
  const reducedMotion = useReducedMotion();
  /* Atropos replaces the tile's element structure, so it must not appear until
   * after hydration — otherwise the prerendered (post-effect) HTML and the
   * first client render disagree about the tree. */
  const tilt = useIsHydrated() && isLargeScreen && !reducedMotion;

  return (
    <section
      ref={sectionRef}
      id="services"
      /* Height is CSS, not a JS-measured prop baked into the prerendered HTML. */
      className="WhatWeDo relative w-full nav-sm:min-h-[100svh] bg-no-repeat bg-cover flex flex-col flex-grow items-center"
      style={{ backgroundImage: `url(${bgServices})` }}>
      <div data-reveal className="mt-[52px] c-md:mt-[56px] c-lg:mt-[90px] c-xl:mt-[125px] text-white flex flex-col items-center justify-center">
        <h2 className="text-36 c-lg:text-50 c-xl:text-60 font-bold">WHAT WE DO?</h2>
      </div>

      <div className="service-grid-width flex flex-1 flex-col items-center justify-start h-full">
        <p data-reveal style={{ "--reveal-delay": "100ms" }} className="text-white text-18 c-md:text-20 c-xl:text-30 px-[24px] mt-[20px] text-center">
          Here are some of the key services we proudly offer our clients!
        </p>

        <div data-reveal style={{ "--reveal-delay": "200ms" }} className="w-full h-full flex flex-1 items-center justify-center mt-[64px] c-lg:mt-0">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-center gap-y-0 w-full list-none">
            {SERVICES.map((service, i) => (
              <ServiceItem
                key={service.name}
                name={service.name}
                image={service.image}
                index={i + 1}
                tilt={tilt}
                onServiceClicked={(e) => onServiceClicked(e, i)}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ServiceItem({ image, name, onServiceClicked, index, tilt }) {
  const rotationAngle = index % 3 === 1 ? 12 : index % 3 === 2 ? 0 : -12;
  const rotationSide = index <= 3 ? "bottom" : "top";

  const itemSize =
    tilt && rotationAngle === 0
      ? index === 2
        ? "aspect-serviceratiom mt-[0.93vw]"
        : index === 5
        ? "aspect-serviceratiom"
        : "aspect-serviceratio"
      : "aspect-serviceratio";

  const paddingStyle = "py-[8px] c-2lg:py-[25px] px-[6px] c-2lg:px-[14px]";
  const transformStyle = tilt
    ? {
        transform: `perspective(1000px) rotateY(${rotationAngle}deg)`,
        transformStyle: "preserve-3d",
        transformOrigin: rotationSide,
      }
    : undefined;

  return (
    <li className={`${itemSize} list-none`}>
      {/* A real <button>, not a div wearing role="button". It also gives the
          popup a correct anchor: the handler now reads currentTarget, which is
          this element, instead of whichever <img> or <p> the click landed on. */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`Open ${name} service details`}
        className={`w-full h-full cursor-pointer flex items-center justify-center ${FOCUS_RING} rounded-[28px]`}
        onClick={onServiceClicked}>
        {tilt ? (
          <span className="block w-full h-full" style={transformStyle}>
            <Atropos className={`w-full h-full ${paddingStyle}`} shadow={false} highlight={true} scale={false}>
              <ServiceItemInside image={image} name={name} />
            </Atropos>
          </span>
        ) : (
          <span className={`block w-full h-full ${paddingStyle}`}>
            <ServiceItemInside image={image} name={name} />
          </span>
        )}
      </button>
    </li>
  );
}

function ServiceItemInside({ image, name }) {
  return (
    <span className="block h-full rounded-[28px] overflow-hidden">
      <span className="block overflow-hidden relative h-full">
        <img src={image} alt="" width={512} height={483} className="w-full h-full object-cover" loading="lazy" />
        <span className="absolute inset-0 flex items-center justify-center h-full w-full">
          <span className="text-36 md:text-42 c-hd:text-36 c-lg:text-42 c-xl:text-56 font-bold text-white text-center tracking-lsservice w-full h-full flex items-center justify-center">
            {name}
          </span>
        </span>
      </span>
    </span>
  );
}
